/**
 * MapLibre sources and style layers for GeoMap's data layers. Each data layer owns one GeoJSON
 * source (updated in place with setData) and a few style layers whose ids start with the data
 * layer's id. Colours arrive in the features (resolved from tokens), so the style only reads
 * them with ['get', 'color'].
 */
import type { ExpressionSpecification, GeoJSONSource, LayerSpecification, Map as MapLibre } from 'maplibre-gl';
import { type GeoLayer, lineFeatures, pointFeatures, shapeFeatures, type GeoTone } from './geo';

export interface LayerColours {
  resolve: (tone: GeoTone | undefined, color: string | null | undefined) => string;
  surface: string;
  text: string;
  primary: string;
}

export const ARROW_IMAGE = 'nova-arrow';
const PREFIX = 'geo-';

export const sourceId = (layerId: string) => `${PREFIX}${layerId}`;
/** Style layers that answer clicks for a data layer. */
export function interactiveLayerIds(layer: GeoLayer): string[] {
  if (!layer.interactive) return [];
  const id = sourceId(layer.id);
  if (layer.kind === 'points') return [`${id}-dots`, ...(layer.cluster ? [`${id}-clusters`] : [])];
  if (layer.kind === 'lines') return [`${id}-line`];
  return [`${id}-fill`];
}

/** A font the base style already uses (its glyphs serve it) — a regular weight when there is
 * one (the first symbol layer is often an italic water label) — or null when it has none. */
export function styleFont(map: MapLibre): string[] | null {
  const style = map.getStyle();
  if (!style?.glyphs) return null;
  let first: string[] | null = null;
  for (const l of style.layers ?? []) {
    const font = l.type === 'symbol' ? l.layout?.['text-font'] : undefined;
    if (!Array.isArray(font) || !font.every((f) => typeof f === 'string')) continue;
    const stack = font as string[];
    if (/regular/i.test(stack[0] ?? '')) return stack;
    first ??= stack;
  }
  return first;
}

/** A white arrow (signed-distance image, tinted per feature with icon-color). */
export function ensureArrowImage(map: MapLibre): void {
  if (map.hasImage(ARROW_IMAGE)) return;
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = 'white';
  ctx.beginPath();
  ctx.moveTo(size / 2, 3);
  ctx.lineTo(size - 7, size - 6);
  ctx.lineTo(size / 2, size - 11);
  ctx.lineTo(7, size - 6);
  ctx.closePath();
  ctx.fill();
  const data = ctx.getImageData(0, 0, size, size);
  map.addImage(ARROW_IMAGE, { width: size, height: size, data: new Uint8Array(data.data.buffer) }, { sdf: true, pixelRatio: 2 });
}

const radiusBySize: ExpressionSpecification = ['match', ['get', 'size'], 'sm', 4, 'lg', 9, 7];

function pointLayers(id: string, layer: Extract<GeoLayer, { kind: 'points' }>, c: LayerColours, font: string[] | null): LayerSpecification[] {
  const notCluster: ExpressionSpecification = ['!', ['has', 'point_count']];
  const out: LayerSpecification[] = [];
  if (layer.cluster) {
    out.push({
      id: `${id}-clusters`,
      type: 'circle',
      source: id,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': c.primary,
        'circle-opacity': 0.88,
        'circle-radius': ['step', ['get', 'point_count'], 14, 10, 18, 50, 23, 200, 28],
        'circle-stroke-width': 3,
        'circle-stroke-color': c.surface,
      },
    });
    if (font) {
      out.push({
        id: `${id}-count`,
        type: 'symbol',
        source: id,
        filter: ['has', 'point_count'],
        layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': font, 'text-size': 12, 'text-allow-overlap': true },
        paint: { 'text-color': c.surface },
      });
    }
  }
  out.push({
    id: `${id}-halo`,
    type: 'circle',
    source: id,
    filter: ['all', notCluster, ['==', ['get', 'selected'], true]],
    paint: { 'circle-color': c.primary, 'circle-opacity': 0.22, 'circle-radius': ['+', radiusBySize, 10] },
  });
  if (layer.arrows) {
    out.push({
      id: `${id}-arrows`,
      type: 'symbol',
      source: id,
      filter: ['all', notCluster, ['==', ['get', 'hasHeading'], true]],
      layout: {
        'icon-image': ARROW_IMAGE,
        'icon-size': ['match', ['get', 'size'], 'lg', 0.85, 0.7],
        'icon-rotate': ['coalesce', ['get', 'heading'], 0],
        'icon-rotation-alignment': 'map',
        'icon-offset': [0, -17],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: { 'icon-color': ['get', 'color'], 'icon-opacity': ['case', ['get', 'muted'], 0.35, 1] },
    });
  }
  out.push({
    id: `${id}-dots`,
    type: 'circle',
    source: id,
    filter: notCluster,
    paint: {
      'circle-color': ['get', 'color'],
      'circle-radius': ['case', ['get', 'selected'], ['+', radiusBySize, 2], radiusBySize],
      'circle-stroke-width': ['case', ['get', 'selected'], 3, 2],
      'circle-stroke-color': c.surface,
      'circle-opacity': ['case', ['get', 'muted'], 0.35, 1],
      'circle-stroke-opacity': ['case', ['get', 'muted'], 0.35, 1],
    },
  });
  if (layer.labels && font) {
    out.push({
      id: `${id}-labels`,
      type: 'symbol',
      source: id,
      filter: notCluster,
      layout: {
        'text-field': ['get', 'label'],
        'text-font': font,
        'text-size': 11,
        'text-offset': [0, 1.35],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: {
        'text-color': c.text,
        'text-halo-color': c.surface,
        'text-halo-width': 1.5,
        'text-opacity':
          layer.labels === 'always'
            ? ['case', ['get', 'muted'], 0.4, 1]
            : ['step', ['zoom'], ['case', ['get', 'selected'], 1, 0], 11, ['case', ['get', 'muted'], 0.4, 1]],
      },
    });
  }
  return out;
}

function lineLayers(id: string, layer: Extract<GeoLayer, { kind: 'lines' }>, c: LayerColours): LayerSpecification[] {
  const width = layer.width ?? 4;
  return [
    {
      id: `${id}-casing`,
      type: 'line',
      source: id,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': c.surface, 'line-width': width + 3, 'line-opacity': 0.9 },
    },
    {
      id: `${id}-line`,
      type: 'line',
      source: id,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': ['get', 'color'], 'line-width': width, ...(layer.dashed ? { 'line-dasharray': [2, 1.5] } : {}) },
    },
  ];
}

function shapeLayers(id: string, layer: Extract<GeoLayer, { kind: 'shapes' }>, c: LayerColours, font: string[] | null): LayerSpecification[] {
  const polygon: ExpressionSpecification = ['==', ['geometry-type'], 'Polygon'];
  const out: LayerSpecification[] = [
    {
      id: `${id}-fill`,
      type: 'fill',
      source: id,
      filter: polygon,
      paint: { 'fill-color': ['get', 'color'], 'fill-opacity': ['case', ['get', 'selected'], 0.28, 0.12] },
    },
    {
      id: `${id}-outline`,
      type: 'line',
      source: id,
      filter: polygon,
      paint: { 'line-color': ['get', 'color'], 'line-width': ['case', ['get', 'selected'], 3, 1.5] },
    },
  ];
  if (layer.labels && font) {
    out.push({
      id: `${id}-labels`,
      type: 'symbol',
      source: id,
      filter: ['==', ['geometry-type'], 'Point'],
      layout: { 'text-field': ['get', 'label'], 'text-font': font, 'text-size': 11, 'text-optional': true },
      paint: { 'text-color': c.text, 'text-halo-color': c.surface, 'text-halo-width': 1.5, 'text-opacity': ['step', ['zoom'], 0, 10, 1] },
    });
  }
  return out;
}

/** Data for a layer, with colours resolved. */
export function layerData(layer: GeoLayer, c: LayerColours) {
  if (layer.kind === 'points') return pointFeatures(layer, c.resolve);
  if (layer.kind === 'lines') return lineFeatures(layer, c.resolve);
  return shapeFeatures(layer, c.resolve);
}

/** A signature of what the style layers depend on: when it changes the layers are rebuilt. */
export function layerSignature(layer: GeoLayer, c: LayerColours, font: string[] | null): string {
  const flags = layer.kind === 'points' ? [layer.cluster, layer.labels, layer.arrows] : layer.kind === 'lines' ? [layer.width, layer.dashed] : [layer.labels];
  return JSON.stringify([layer.kind, flags, c.surface, c.text, c.primary, font]);
}

/**
 * Make the source and style layers of every data layer exist, in order, and push the data of
 * the layers in `dirty` (all when omitted).
 */
export function syncLayers(map: MapLibre, layers: GeoLayer[], c: LayerColours, applied: Map<string, string>, dirty?: Set<string>): void {
  const font = styleFont(map);
  ensureArrowImage(map);
  const wanted = new Set(layers.map((l) => sourceId(l.id)));
  // Drop layers that are no longer asked for.
  for (const [sid] of applied) {
    if (wanted.has(sid)) continue;
    removeLayer(map, sid);
    applied.delete(sid);
  }
  let rebuilt = false;
  for (const layer of layers) {
    const sid = sourceId(layer.id);
    const sig = layerSignature(layer, c, font);
    if (applied.get(sid) !== sig || !map.getSource(sid)) {
      rebuilt = true;
      removeLayer(map, sid);
      map.addSource(sid, {
        type: 'geojson',
        data: layerData(layer, c),
        ...(layer.kind === 'points' && layer.cluster ? { cluster: true, clusterRadius: 44, clusterMaxZoom: 13 } : {}),
      });
      const specs =
        layer.kind === 'points' ? pointLayers(sid, layer, c, font) : layer.kind === 'lines' ? lineLayers(sid, layer, c) : shapeLayers(sid, layer, c, font);
      for (const spec of specs) map.addLayer(spec);
      applied.set(sid, sig);
    } else if (!dirty || dirty.has(layer.id)) {
      void (map.getSource(sid) as GeoJSONSource).setData(layerData(layer, c));
    }
  }
  // Keep the stacking order of the data layers as given (later on top).
  if (!rebuilt) return;
  for (const layer of layers) {
    const sid = sourceId(layer.id);
    for (const l of map.getStyle().layers ?? []) if (l.id.startsWith(`${sid}-`)) map.moveLayer(l.id);
  }
}

function removeLayer(map: MapLibre, sid: string): void {
  for (const l of map.getStyle()?.layers ?? []) if (l.id.startsWith(`${sid}-`)) map.removeLayer(l.id);
  if (map.getSource(sid)) map.removeSource(sid);
}
